import React from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { useQuery } from "@tanstack/react-query";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { TabView, TabPanel } from "primereact/tabview";
import formService from "../../services/forms/form.service";
import { useToast } from "../../utils/toast";

const CevreselGostergelerRapor = () => {
  const toast = useToast();
  const {
    data: raporData,
    refetch: refetchRapor,
    isLoading: raporResponseLoading
  } = useQuery({
    queryKey: ["cevreselGostergelerRapor"],
    gcTime: 1000 * 60 * 11,
    refetchOnMount: false,
    queryFn: () => formService.cevreselGostergelerAnketRapor()
  });

  const getChartData = () => {
    console.log("Getting chart data from:", raporData);
    if (!raporData?.result?.data?.cevresel_alan_puanlari) {
      console.log("No cevresel_alan_puanlari found");
      return null;
    }

    const data = raporData.result.data.cevresel_alan_puanlari;
    const labels = Object.keys(data).map((key) => {
      const alanAdi =
        {
          ekonomi_verimlilik: "Ekonominin Çevresel ve Kaynak Verimliliği",
          dogal_varlik: "Doğal Varlık Tabanı",
          yasam_kalitesi: "Yaşam Kalitesinin Çevresel Boyutu",
          ekonomik_firsatlar: "Ekonomik Fırsatlar ve Politika Yanıtları"
        }[key] || key;
      return alanAdi;
    });

    const datasets = [
      {
        label: "Hazırlık",
        data: Object.values(data).map((item: any) => item.hazirlik),
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 2
      },
      {
        label: "Algı",
        data: Object.values(data).map((item: any) => item.algı),
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 2
      },
      {
        label: "Yetkinlik",
        data: Object.values(data).map((item: any) => item.yetkinlik),
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2
      },
      {
        label: "İlerleme",
        data: Object.values(data).map((item: any) => item.ilerleme),
        backgroundColor: "rgba(255, 159, 64, 0.2)",
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 2
      },
      {
        label: "Ortalama",
        data: Object.values(data).map((item: any) => item.ortalama),
        backgroundColor: "rgba(153, 102, 255, 0.2)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 3,
        type: "line"
      }
    ];

    return {
      labels,
      datasets
    };
  };

  const getRadarChartData = () => {
    if (!raporData?.result?.data?.cevresel_alan_puanlari) return null;

    const data = raporData.result.data.cevresel_alan_puanlari;
    const labels = Object.keys(data).map((key) => {
      const alanAdi =
        {
          ekonomi_verimlilik: "Ekonominin Çevresel ve Kaynak Verimliliği",
          dogal_varlik: "Doğal Varlık Tabanı",
          yasam_kalitesi: "Yaşam Kalitesinin Çevresel Boyutu",
          ekonomik_firsatlar: "Ekonomik Fırsatlar ve Politika Yanıtları"
        }[key] || key;
      return alanAdi;
    });

    return {
      labels,
      datasets: [
        {
          label: "Ortalama Puanlar",
          data: Object.values(data).map((item: any) => item.ortalama),
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderColor: "rgba(34, 197, 94, 1)",
          borderWidth: 2,
          pointBackgroundColor: "rgba(34, 197, 94, 1)",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "rgba(34, 197, 94, 1)"
        }
      ]
    };
  };

  const getDetailedRadarChartData = (categoryKey: string) => {
    const detayliAnaliz = raporData?.result?.data?.detayli_analiz;
    if (!detayliAnaliz) return null;

    const kategori = detayliAnaliz[categoryKey as keyof typeof detayliAnaliz];
    if (!kategori?.alt_kategoriler) return null;

    const altKategoriler = kategori.alt_kategoriler;
    const labels = Object.keys(altKategoriler).map((key) => {
      const altKategoriAdi =
        {
          karbon_verimliligi: "Karbon Verimliliği",
          enerji_verimliligi: "Enerji Verimliliği",
          malzeme_verimliligi: "Malzeme Verimliliği",
          atik_yogunlugu: "Atık Yoğunluğu",
          geri_donusum_oranlari: "Geri Dönüşüm Oranları",
          besin_akis_dengeleri: "Besin Akışları ve Dengeleri",
          cevre_duyarli_verimlilik: "Çevreye Duyarlı Verimlilik",
          arazi_ortusu_donusumu: "Arazi Örtüsü Dönüşümü",
          kentsel_alanlar_payi: "Kentsel Alanların Payı",
          orman_alani_orani: "Orman Alanı Oranı",
          orman_kaynak_kullanim: "Orman Kaynak Kullanımı",
          surdurulebilir_orman_sertifikasi: "Sürdürülebilir Orman Sertifikası",
          tatli_su_kaynaklari: "Tatlı Su Kaynakları",
          biyolojik_cesitlilik: "Biyolojik Çeşitlilik",
          sanitasyon_erisimi: "Sanitasyon Erişimi",
          pm25_maruziyeti: "PM2.5 Maruziyeti",
          hava_kirliligi_saglik: "Hava Kirliliği ve Sağlık",
          su_temini_sanitasyon: "Su Temini ve Sanitasyon",
          ozon_maruz_kalimi: "Ozon Maruz Kalımı",
          cevre_urunleri_pazarlari: "Çevre Ürünleri Pazarları",
          cevre_sektor_istihdam: "Çevre Sektörü İstihdam",
          uluslararasi_finansal_akis: "Uluslararası Finansal Akış",
          vergi_subsidyonlar: "Vergi ve Sübvansiyonlar",
          cevre_teknoloji_inovasyon: "Çevre Teknolojisi ve İnovasyon",
          yonetmelik_yonetim: "Yönetmelik ve Yönetim",
          egitim_beceri_gelistirme: "Eğitim ve Beceri Geliştirme"
        }[key] || key;
      return altKategoriAdi;
    });

    const colors = {
      ekonomi_verimlilik: {
        backgroundColor: "rgba(34, 197, 94, 0.2)",
        borderColor: "rgba(34, 197, 94, 1)",
        pointBackgroundColor: "rgba(34, 197, 94, 1)",
        pointHoverBorderColor: "rgba(34, 197, 94, 1)"
      },
      dogal_varlik: {
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        borderColor: "rgba(59, 130, 246, 1)",
        pointBackgroundColor: "rgba(59, 130, 246, 1)",
        pointHoverBorderColor: "rgba(59, 130, 246, 1)"
      },
      yasam_kalitesi: {
        backgroundColor: "rgba(245, 158, 11, 0.2)",
        borderColor: "rgba(245, 158, 11, 1)",
        pointBackgroundColor: "rgba(245, 158, 11, 1)",
        pointHoverBorderColor: "rgba(245, 158, 11, 1)"
      },
      ekonomik_firsatlar: {
        backgroundColor: "rgba(147, 51, 234, 0.2)",
        borderColor: "rgba(147, 51, 234, 1)",
        pointBackgroundColor: "rgba(147, 51, 234, 1)",
        pointHoverBorderColor: "rgba(147, 51, 234, 1)"
      }
    };

    const categoryColors = colors[categoryKey as keyof typeof colors] || colors.ekonomi_verimlilik;

    return {
      labels,
      datasets: [
        {
          label: "Ortalama Puanlar",
          data: Object.values(altKategoriler).map((item: any) => item.ortalama),
          backgroundColor: categoryColors.backgroundColor,
          borderColor: categoryColors.borderColor,
          borderWidth: 2,
          pointBackgroundColor: categoryColors.pointBackgroundColor,
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: categoryColors.pointHoverBorderColor
        }
      ]
    };
  };

  const getSubcategoryChartData = (categoryKey: string) => {
    const detayliAnaliz = raporData?.result?.data?.detayli_analiz;
    if (!detayliAnaliz) return null;

    const kategori = detayliAnaliz[categoryKey as keyof typeof detayliAnaliz];
    if (!kategori?.alt_kategoriler) return null;

    const altKategoriler = kategori.alt_kategoriler;
    const labels = Object.keys(altKategoriler).map((key) => {
      const altKategoriAdi =
        {
          karbon_verimliligi: "Karbon Verimliliği",
          enerji_verimliligi: "Enerji Verimliliği",
          malzeme_verimliligi: "Malzeme Verimliliği",
          atik_yogunlugu: "Atık Yoğunluğu",
          geri_donusum_oranlari: "Geri Dönüşüm Oranları",
          besin_akis_dengeleri: "Besin Akışları ve Dengeleri",
          cevre_duyarli_verimlilik: "Çevreye Duyarlı Verimlilik",
          arazi_ortusu_donusumu: "Arazi Örtüsü Dönüşümü",
          kentsel_alanlar_payi: "Kentsel Alanların Payı",
          orman_alani_orani: "Orman Alanı Oranı",
          orman_kaynak_kullanim: "Orman Kaynak Kullanımı",
          surdurulebilir_orman_sertifikasi: "Sürdürülebilir Orman Sertifikası",
          tatli_su_kaynaklari: "Tatlı Su Kaynakları",
          biyolojik_cesitlilik: "Biyolojik Çeşitlilik",
          sanitasyon_erisimi: "Sanitasyon Erişimi",
          pm25_maruziyeti: "PM2.5 Maruziyeti",
          hava_kirliligi_saglik: "Hava Kirliliği ve Sağlık",
          su_temini_sanitasyon: "Su Temini ve Sanitasyon",
          ozon_maruz_kalimi: "Ozon Maruz Kalımı",
          cevre_urunleri_pazarlari: "Çevre Ürünleri Pazarları",
          cevre_sektor_istihdam: "Çevre Sektörü İstihdam",
          uluslararasi_finansal_akis: "Uluslararası Finansal Akış",
          vergi_subsidyonlar: "Vergi ve Sübvansiyonlar",
          cevre_teknoloji_inovasyon: "Çevre Teknolojisi ve İnovasyon",
          yonetmelik_yonetim: "Yönetmelik ve Yönetim",
          egitim_beceri_gelistirme: "Eğitim ve Beceri Geliştirme"
        }[key] || key;
      return altKategoriAdi;
    });

    return {
      labels,
      datasets: [
        {
          label: "Ortalama",
          data: Object.values(altKategoriler).map((item: any) => item.ortalama),
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderColor: "rgba(34, 197, 94, 1)",
          borderWidth: 2
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 3,
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0
        }
      }
    },
    plugins: {
      legend: {
        position: "top" as const
      },
      title: {
        display: false
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10
      }
    }
  };

  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        position: "top" as const
      },
      title: {
        display: true,
        text: "Çevresel Alan Ortalama Puanları (Örümcek Ağı)"
      }
    }
  };

  const subcategoryChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    scales: {
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0
        }
      }
    },
    plugins: {
      legend: {
        position: "top" as const
      },
      title: {
        display: false
      }
    }
  };

  if (raporResponseLoading) {
    return (
      <div className="flex justify-content-center">
        <ProgressSpinner />
      </div>
    );
  }

  if (!raporData?.result?.data) {
    return (
      <div className="flex justify-content-center">
        <Message severity="info" text="Henüz anket verisi bulunmamaktadır." />
      </div>
    );
  }

  const chartData = getChartData();
  const radarChartData = getRadarChartData();
  const analizOzeti = raporData?.result?.data?.analiz_ozeti;

  return (
    <div className="card">
      <div className="w-full surface-card">
        <div className="text-center mb-4">
          <h2 className="text-900 text-2xl sm:text-3xl font-semibold mb-2">Çevresel Göstergeler Anketi Raporu</h2>
          <p className="text-600 text-sm sm:text-base m-0">OSB bazında çevresel göstergeler puanlarının detaylı analizi ve görselleştirmesi</p>
        </div>

        {/* Yenile Butonu */}
        <Card title="Rapor Kontrolleri" className="mb-4">
          <div className="flex align-items-center gap-3">
            <Button label="Yenile" onClick={() => refetchRapor()} icon="pi pi-refresh" />
          </div>
        </Card>

        {/* Analiz Özeti */}
        {analizOzeti && (
          <Card title="Analiz Özeti" className="mb-4">
            <div className="grid">
              <div className="col-12 md:col-4">
                <div className="p-3 border-1 surface-border border-round text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {analizOzeti.en_yuksek_kategori === "ekonomi_verimlilik"
                      ? "Ekonominin Çevresel ve Kaynak Verimliliği"
                      : analizOzeti.en_yuksek_kategori === "dogal_varlik"
                        ? "Doğal Varlık Tabanı"
                        : analizOzeti.en_yuksek_kategori === "yasam_kalitesi"
                          ? "Yaşam Kalitesinin Çevresel Boyutu"
                          : analizOzeti.en_yuksek_kategori === "ekonomik_firsatlar"
                            ? "Ekonomik Fırsatlar ve Politika Yanıtları"
                            : analizOzeti.en_yuksek_kategori}
                  </div>
                  <div className="text-600">En Yüksek Kategori</div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="p-3 border-1 surface-border border-round text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {analizOzeti.en_dusuk_kategori === "ekonomi_verimlilik"
                      ? "Ekonominin Çevresel ve Kaynak Verimliliği"
                      : analizOzeti.en_dusuk_kategori === "dogal_varlik"
                        ? "Doğal Varlık Tabanı"
                        : analizOzeti.en_dusuk_kategori === "yasam_kalitesi"
                          ? "Yaşam Kalitesinin Çevresel Boyutu"
                          : analizOzeti.en_dusuk_kategori === "ekonomik_firsatlar"
                            ? "Ekonomik Fırsatlar ve Politika Yanıtları"
                            : analizOzeti.en_dusuk_kategori}
                  </div>
                  <div className="text-600">En Düşük Kategori</div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="p-3 border-1 surface-border border-round text-center">
                  <div className="text-2xl font-bold text-blue-600">{analizOzeti.genel_ortalama.toFixed(2)}</div>
                  <div className="text-600">Genel Ortalama</div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* İstatistikler */}
        <Card title="Genel İstatistikler" className="mb-4">
          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="p-3 border-1 surface-border border-round text-center">
                <div className="text-2xl font-bold text-primary">
                  {raporData?.result?.data?.toplam_anket_sayisi ?? "—"}
                </div>
                <div className="text-600">Toplam Anket</div>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-3 border-1 surface-border border-round text-center">
                <div className="text-2xl font-bold text-green-600">
                  {raporData?.result?.data?.osb_listesi?.length || 0}
                </div>
                <div className="text-600">OSB Sayısı</div>
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-3 border-1 surface-border border-round text-center">
                <div className="text-2xl font-bold text-blue-600">104</div>
                <div className="text-600">Toplam Soru</div>
              </div>
            </div>
          </div>
        </Card>

        {/* OSB Listesi */}
        <Card title="Katılım Gösteren OSB'ler" className="mb-4">
          <div className="grid">
            {raporData?.result?.data?.osb_listesi?.map((osb: any, index: number) => (
              <div key={index} className="col-12 md:col-3">
                <div className="p-3 border-1 surface-border border-round text-center">
                  <div className="text-sm font-medium text-600">{osb.osb_adi}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Ana Kategoriler Tablosu */}
        <Card title="Ana Kategoriler Puanları" className="mb-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-bottom-1 surface-border">
                  <th className="text-left p-3">Çevresel Alan</th>
                  <th className="text-center p-3">Hazırlık</th>
                  <th className="text-center p-3">Algı</th>
                  <th className="text-center p-3">Yetkinlik</th>
                  <th className="text-center p-3">İlerleme</th>
                  <th className="text-center p-3">Ortalama</th>
                </tr>
              </thead>
              <tbody>
                {raporData?.result?.data?.cevresel_alan_puanlari &&
                  Object.entries(raporData.result.data.cevresel_alan_puanlari).map(([key, value]: [string, any]) => {
                    const alanAdi =
                      {
                        ekonomi_verimlilik: "Ekonominin Çevresel ve Kaynak Verimliliği",
                        dogal_varlik: "Doğal Varlık Tabanı",
                        yasam_kalitesi: "Yaşam Kalitesinin Çevresel Boyutu",
                        ekonomik_firsatlar: "Ekonomik Fırsatlar ve Politika Yanıtları"
                      }[key] || key;

                    return (
                      <tr key={key} className="border-bottom-1 surface-border">
                        <td className="p-3 font-medium">{alanAdi}</td>
                        <td className="text-center p-3">{value.hazirlik.toFixed(2)}</td>
                        <td className="text-center p-3">{value.algı.toFixed(2)}</td>
                        <td className="text-center p-3">{value.yetkinlik.toFixed(2)}</td>
                        <td className="text-center p-3">{value.ilerleme.toFixed(2)}</td>
                        <td className="text-center p-3 font-bold text-primary">{value.ortalama.toFixed(2)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Detaylı Analiz TabView */}
        {raporData?.result?.data?.detayli_analiz && (
          <Card title="Detaylı Alt Kategori Analizi" className="mb-4">
            <TabView>
              <TabPanel header="Ekonominin Çevresel ve Kaynak Verimliliği">
                <div className="grid">
                  <div className="col-12 lg:col-8">
                    <div style={{ height: "300px" }}>
                      {getSubcategoryChartData("ekonomi_verimlilik") && (
                        <Chart
                          type="bar"
                          data={getSubcategoryChartData("ekonomi_verimlilik")!}
                          options={subcategoryChartOptions}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-12 lg:col-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-bottom-1 surface-border">
                            <th className="text-left p-2">Alt Kategori</th>
                            <th className="text-center p-2">Ortalama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(raporData.result.data.detayli_analiz.ekonomi_verimlilik.alt_kategoriler).map(
                            ([key, value]: [string, any]) => {
                              const altKategoriAdi =
                                {
                                  karbon_verimliligi: "Karbon Verimliliği",
                                  enerji_verimliligi: "Enerji Verimliliği",
                                  malzeme_verimliligi: "Malzeme Verimliliği",
                                  atik_yogunlugu: "Atık Yoğunluğu",
                                  geri_donusum_oranlari: "Geri Dönüşüm Oranları",
                                  besin_akis_dengeleri: "Besin Akışları ve Dengeleri",
                                  cevre_duyarli_verimlilik: "Çevreye Duyarlı Verimlilik"
                                }[key] || key;

                              return (
                                <tr key={key} className="border-bottom-1 surface-border">
                                  <td className="p-2 text-sm">{altKategoriAdi}</td>
                                  <td className="text-center p-2 font-bold">{value.ortalama.toFixed(2)}</td>
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Doğal Varlık Tabanı">
                <div className="grid">
                  <div className="col-12 lg:col-8">
                    <div style={{ height: "300px" }}>
                      {getSubcategoryChartData("dogal_varlik") && (
                        <Chart
                          type="bar"
                          data={getSubcategoryChartData("dogal_varlik")!}
                          options={subcategoryChartOptions}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-12 lg:col-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-bottom-1 surface-border">
                            <th className="text-left p-2">Alt Kategori</th>
                            <th className="text-center p-2">Ortalama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(raporData.result.data.detayli_analiz.dogal_varlik.alt_kategoriler).map(
                            ([key, value]: [string, any]) => {
                              const altKategoriAdi =
                                {
                                  arazi_ortusu_donusumu: "Arazi Örtüsü Dönüşümü",
                                  kentsel_alanlar_payi: "Kentsel Alanların Payı",
                                  orman_alani_orani: "Orman Alanı Oranı",
                                  orman_kaynak_kullanim: "Orman Kaynak Kullanımı",
                                  surdurulebilir_orman_sertifikasi: "Sürdürülebilir Orman Sertifikası",
                                  tatli_su_kaynaklari: "Tatlı Su Kaynakları",
                                  biyolojik_cesitlilik: "Biyolojik Çeşitlilik"
                                }[key] || key;

                              return (
                                <tr key={key} className="border-bottom-1 surface-border">
                                  <td className="p-2 text-sm">{altKategoriAdi}</td>
                                  <td className="text-center p-2 font-bold">{value.ortalama.toFixed(2)}</td>
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Yaşam Kalitesinin Çevresel Boyutu">
                <div className="grid">
                  <div className="col-12 lg:col-8">
                    <div style={{ height: "300px" }}>
                      {getSubcategoryChartData("yasam_kalitesi") && (
                        <Chart
                          type="bar"
                          data={getSubcategoryChartData("yasam_kalitesi")!}
                          options={subcategoryChartOptions}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-12 lg:col-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-bottom-1 surface-border">
                            <th className="text-left p-2">Alt Kategori</th>
                            <th className="text-center p-2">Ortalama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(raporData.result.data.detayli_analiz.yasam_kalitesi.alt_kategoriler).map(
                            ([key, value]: [string, any]) => {
                              const altKategoriAdi =
                                {
                                  sanitasyon_erisimi: "Sanitasyon Erişimi",
                                  pm25_maruziyeti: "PM2.5 Maruziyeti",
                                  hava_kirliligi_saglik: "Hava Kirliliği ve Sağlık",
                                  su_temini_sanitasyon: "Su Temini ve Sanitasyon",
                                  ozon_maruz_kalimi: "Ozon Maruz Kalımı"
                                }[key] || key;

                              return (
                                <tr key={key} className="border-bottom-1 surface-border">
                                  <td className="p-2 text-sm">{altKategoriAdi}</td>
                                  <td className="text-center p-2 font-bold">{value.ortalama.toFixed(2)}</td>
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Ekonomik Fırsatlar ve Politika Yanıtları">
                <div className="grid">
                  <div className="col-12 lg:col-8">
                    <div style={{ height: "300px" }}>
                      {getSubcategoryChartData("ekonomik_firsatlar") && (
                        <Chart
                          type="bar"
                          data={getSubcategoryChartData("ekonomik_firsatlar")!}
                          options={subcategoryChartOptions}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-12 lg:col-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-bottom-1 surface-border">
                            <th className="text-left p-2">Alt Kategori</th>
                            <th className="text-center p-2">Ortalama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(raporData.result.data.detayli_analiz.ekonomik_firsatlar.alt_kategoriler).map(
                            ([key, value]: [string, any]) => {
                              const altKategoriAdi =
                                {
                                  cevre_urunleri_pazarlari: "Çevre Ürünleri Pazarları",
                                  cevre_sektor_istihdam: "Çevre Sektörü İstihdam",
                                  uluslararasi_finansal_akis: "Uluslararası Finansal Akış",
                                  vergi_subsidyonlar: "Vergi ve Sübvansiyonlar",
                                  cevre_teknoloji_inovasyon: "Çevre Teknolojisi ve İnovasyon",
                                  yonetmelik_yonetim: "Yönetmelik ve Yönetim",
                                  egitim_beceri_gelistirme: "Eğitim ve Beceri Geliştirme"
                                }[key] || key;

                              return (
                                <tr key={key} className="border-bottom-1 surface-border">
                                  <td className="p-2 text-sm">{altKategoriAdi}</td>
                                  <td className="text-center p-2 font-bold">{value.ortalama.toFixed(2)}</td>
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabView>
          </Card>
        )}

        {/* Detaylı Radar Grafikleri */}
        {raporData?.result?.data?.detayli_analiz && (
          <Card title="Detaylı Radar Grafikleri (Örümcek Ağı)" className="mb-4">
            <TabView>
              <TabPanel header="Ekonominin Çevresel ve Kaynak Verimliliği">
                <div className="grid">
                  <div className="col-12">
                    <div style={{ height: "400px" }}>
                      {getDetailedRadarChartData("ekonomi_verimlilik") && (
                        <Chart
                          type="radar"
                          data={getDetailedRadarChartData("ekonomi_verimlilik")!}
                          options={radarChartOptions}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Doğal Varlık Tabanı">
                <div className="grid">
                  <div className="col-12">
                    <div style={{ height: "400px" }}>
                      {getDetailedRadarChartData("dogal_varlik") && (
                        <Chart
                          type="radar"
                          data={getDetailedRadarChartData("dogal_varlik")!}
                          options={radarChartOptions}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Yaşam Kalitesinin Çevresel Boyutu">
                <div className="grid">
                  <div className="col-12">
                    <div style={{ height: "400px" }}>
                      {getDetailedRadarChartData("yasam_kalitesi") && (
                        <Chart
                          type="radar"
                          data={getDetailedRadarChartData("yasam_kalitesi")!}
                          options={radarChartOptions}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Ekonomik Fırsatlar ve Politika Yanıtları">
                <div className="grid">
                  <div className="col-12">
                    <div style={{ height: "400px" }}>
                      {getDetailedRadarChartData("ekonomik_firsatlar") && (
                        <Chart
                          type="radar"
                          data={getDetailedRadarChartData("ekonomik_firsatlar")!}
                          options={radarChartOptions}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabView>
          </Card>
        )}

        {/* Grafikler */}
        {chartData && (
          <Card title="Çevresel Alan Puanları Grafiği" className="mb-4">
            <div style={{ height: "400px" }}>
              <Chart type="bar" data={chartData} options={chartOptions} />
            </div>
          </Card>
        )}

        {radarChartData && (
          <Card title="Genel Çevresel Alan Ortalama Puanları" className="mb-4">
            <div style={{ height: "400px" }}>
              <Chart type="radar" data={radarChartData} options={radarChartOptions} />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CevreselGostergelerRapor;
